import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { Redirect, useHistory } from 'react-router-dom';
import isEmail from 'validator/lib/isEmail';
import {
  ErrorMessage,
  Eye,
  EyeActive,
  Flex,
  Form,
  FormGroup,
  Input,
  Label,
  ModalCol,
  ModalHeading,
} from '../../business/login/styled';
import Lang from '@lang/en';
import Loader from '@app/common/loadData/Loader';
import { LoginData } from '../../session/login/LoginData';
import useLoginForm from './useLoginForm';
import Links from '../routes/links';
import Button from '@root/src/app/designSystem/buttons';
import styled from 'styled-components';

const StyledSubmitButton = styled(Button)`
  margin-top: 32px;
`;

interface Props {
  show: boolean;
  handleClose: () => void;
}

const LoginForm: React.FC<Props> = ({ show, handleClose }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginData>();

  const [showPassword, setShowPassword] = useState(false);

  const togglePassword = (show: boolean): void => {
    setShowPassword(show);
  };

  const { isLogged, loading, error, clearError, saveForm } = useLoginForm();
  const history = useHistory();

  if (show && isLogged) {
    return <Redirect to={Links.dashboard} />;
  }

  const email = register('email', {
    required: true,
    validate: (value: string) => isEmail(value.trim()) || Lang.invalidEmailText,
  });

  const password = register('password', {
    required: true,
  });

  return (
    <ModalCol>
      <Modal
        show={true}
        onHide={(): void => {
          history.push(Links.signIn);
        }}
        data-testid="login-modal"
        dialogClassName="login-modal"
        contentClassName="no-header"
      >
        <Modal.Body>
          <ModalHeading data-testid="healthcheck-signin-header">
            {Lang.signinText}
          </ModalHeading>
          <Form>
            <Flex>
              <FormGroup>
                <Label>{Lang.loginEmailContent}</Label>
                <Input
                  placeholder={Lang.loginEmailPlaceholder}
                  data-testid="login-email"
                  {...email}
                  onChange={(event) => {
                    email.onChange(event);
                    clearError();
                  }}
                />
                {errors.email && (
                  <ErrorMessage data-testid="login-email-required-error">
                    {errors.email.message || Lang.requiredText}
                  </ErrorMessage>
                )}
              </FormGroup>
              <FormGroup>
                <Label>{Lang.loginPasswordContent}</Label>
                <Input
                  placeholder={Lang.loginPasswordPlaceholder}
                  data-testid="login-password"
                  type={showPassword ? 'text' : 'password'}
                  {...password}
                  onChange={(event) => {
                    password.onChange(event);
                    clearError();
                  }}
                />
                {error && (
                  <ErrorMessage data-testid="login-invalid-credentials">
                    {Lang.invalidCredentialsText}
                  </ErrorMessage>
                )}
                {errors.password && (
                  <ErrorMessage data-testid="login-password-required-error">
                    {errors.password.message || Lang.requiredText}
                  </ErrorMessage>
                )}
                {showPassword ? (
                  <EyeActive
                    data-testid="login-hide-password"
                    onClick={(): void => togglePassword(!showPassword)}
                  />
                ) : (
                  <Eye
                    data-testid="login-show-password"
                    onClick={(): void => togglePassword(!showPassword)}
                  />
                )}
              </FormGroup>
            </Flex>
            {loading && <Loader />}
            <StyledSubmitButton
              onClick={handleSubmit(saveForm)}
              data-testid="login-sign-in"
              type="primary"
              label={Lang.signinText}
            />
          </Form>
        </Modal.Body>
      </Modal>
    </ModalCol>
  );
};

export default LoginForm;
